import { useFormContext, FieldValues, Path } from 'react-hook-form'
import { z } from 'zod'
import styles from './styles.module.scss'
import { FieldErrorMessage } from '../shared/ErrorMessage'
import labelStyles from '../shared/fieldLabel.module.scss'
import fieldWrapperStyles from '../shared/fieldWrapper.module.scss'
import { useState } from 'react'
import classNames from 'classnames'

type TextareaControllerProps<T extends FieldValues> = {
  id: Path<T>
  name: string
  required?: boolean
  placeholder?: string
  disabled?: boolean
  maxLength?: number
  textPosition?: 'left' | 'right' | 'center'
  showDefaultLabel?: boolean
}

export function TextareaField<T extends FieldValues>({
  id,
  name,
  required = false,
  placeholder,
  disabled = false,
  maxLength = 200,
  textPosition = 'left',
  showDefaultLabel = false
}: TextareaControllerProps<T>) {
  const {
    register,
    watch,
    formState: { errors }
  } = useFormContext<T>()
  const [focused, setFocused] = useState(false)

  console.log({ showDefaultLabel: !showDefaultLabel })

  const value = watch(id) ?? ''
  const charCount = value.toString().length

  const hasValue =
    value !== undefined &&
    value !== null &&
    (typeof value === 'number' || String(value).trim().length > 0)

  const isActive = focused || hasValue

  const textareaClasses = classNames(styles.Textarea, styles[textPosition], {
    [styles.disabled]: disabled,
    [styles.focused]: isActive,
    [styles.error]: errors[id] && !disabled
  })

  const labelClasses = classNames({
    [labelStyles.disabled]: disabled,
    [labelStyles.focused]: isActive,
    [labelStyles.required]: required
  })

  return (
    <div
      className={`${fieldWrapperStyles.FieldWrapperTextarea} ${
        !showDefaultLabel ? fieldWrapperStyles.FieldLabel__fluid : ''
      }`}
    >
      <label
        htmlFor={id}
        className={`${labelStyles.FieldLabel} ${
          labelStyles.FieldLabelTextarea
        } ${!showDefaultLabel ? labelStyles.FieldLabel__fluid : ''} ${
          isActive ? labelStyles.focused : ''
        }`}
      >
        <span className={`${labelStyles.FieldLabel__name} ${labelClasses}`}>
          {name}
        </span>
      </label>
      <div className={styles.FieldLabelCount}>
        {charCount} / {maxLength}
      </div>
      <textarea
        {...register(id)}
        id={id}
        aria-label={name}
        placeholder={placeholder}
        onFocus={() => !disabled && setFocused(true)}
        onBlur={() => !disabled && setFocused(false)}
        className={textareaClasses}
        autoComplete="off"
        disabled={disabled}
      />

      {errors[id] && !disabled && (
        <FieldErrorMessage name={id} errors={errors} />
      )}
    </div>
  )
}

TextareaField.getSchema = (required: boolean, maxCount = 200) => {
  if (required) {
    return z
      .string()
      .min(1, 'This field is required')
      .max(maxCount, `Must be ${maxCount} characters or fewer`)
  } else {
    return z
      .string()
      .max(maxCount, `Must be ${maxCount} characters or fewer`)
      .transform(val => (val === '' ? undefined : val))
      .optional()
  }
}
